import { getPlaceholderAvatar } from '@/core/avatar';
import { execute } from '@/graphql/execute';
import { getBatch, getBatches } from '@/queries/attachment.query';
import {
  Alarm,
  ArrowUpCircleSolid,
  Calendar,
  CheckCircleSolid,
  ClockCircleSolid,
  DangerCircleSolid,
} from '@mynaui/icons-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { Loader, Loader2 } from 'lucide-react';
import { FunctionComponent, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { Duration } from './Duration';
import PortalTooltip from './PortalTooltip';
import { Label } from './ui/label';

export const formatDateTime = (date: Date, t?: (key: string, options?: any) => string) => {
  if (dayjs(date).isBefore(dayjs().subtract(1, 'week'))) {
    return dayjs(date).format('YYYY-MM-DD HH:mm');
  }

  const now = dayjs();
  const dateObj = dayjs(date);
  const diffMinutes = now.diff(dateObj, 'minute');
  if (diffMinutes < -600) {
    return dateObj.format('DD/MM/YYYY [at] HH:mm');
  } else if (diffMinutes < -60) {
    return dateObj.format('dddd [at] HH:mm');
  } else if (diffMinutes < 0) {
    return t ? t('batchList.inMinutes', { count: Math.abs(diffMinutes) }) : `In ${Math.abs(diffMinutes)} minutes`;
  } else if (diffMinutes < 60) {
    return t ? t('batchList.minutesAgo', { count: diffMinutes }) : `${diffMinutes} minutes ago`;
  } else {
    return dateObj.format('dddd [at] HH:mm');
  }
};

export const batchStatusMap = {
  UPLOADING: {
    label: 'Uploading',
    className: 'bg-yellow-500',
    icon: <ArrowUpCircleSolid className='text-slate-400' />,
  },
  WAITING: {
    label: 'Waiting',
    className: 'bg-gray-500 text-white',
    icon: <ClockCircleSolid className='text-amber-500' />,
  },
  PROCESSING: {
    label: 'Processing',
    className: 'bg-blue-500 text-white',
    icon: <Loader2 className='animate-spin' />,
  },
  PROCESSED: {
    label: 'Processed',
    className: 'bg-green-500 text-white',
    icon: <CheckCircleSolid className='text-emerald-500' />,
  },
  FAILED: {
    label: 'Failed',
    className: 'bg-red-500 text-white',
    icon: <DangerCircleSolid className='text-rose-500' />,
  },
};

interface iBatchListProps {}

const BatchList: FunctionComponent<iBatchListProps> = () => {
  const { t } = useTranslation('components');
  const { projectId } = useParams();
  const queryClient = useQueryClient();
  const prefetchTimeout = useRef<ReturnType<typeof setTimeout>>();
  const { data: batchesData, isLoading } = useQuery({
    queryKey: ['batches'],
    queryFn: () => execute(getBatches, { projectId: projectId! }),
  });

  const batches = batchesData?.batches;

  if (isLoading) return <Loader className='animate-spin' />;
  return (
    <>
      <Label>{t('batchList.batches')}</Label>
      <div className='flex flex-col border border-slate-200/70 rounded-md bg-white dark:bg-stone-900 dark:border-stone-800 mt-2 overflow-hidden'>
        {batches?.length === 0 && (
          <div className='flex flex-col items-center justify-center py-4'>
            <span className='text-slate-500 dark:text-stone-200 text-sm font-medium'>{t('batchList.noBatches')}</span>
            <p className='text-slate-500/75 dark:text-stone-400 text-xs font-medium'>{t('batchList.noBatchesDescription')}</p>
          </div>
        )}
        {batches?.map((batch) => (
          <Link
            key={batch.id}
            className='flex gap-2 border-b border-slate-200/70 last:border-b-0 px-2 dark:border-stone-800 hover:bg-slate-50 dark:hover:bg-stone-800'
            to={`/projects/${projectId}/upload/batch/${batch.id}`}
            onMouseEnter={() => {
              clearTimeout(prefetchTimeout.current);
              prefetchTimeout.current = setTimeout(() => {
                queryClient.prefetchQuery({
                  queryKey: ['batch', batch.id],
                  queryFn: () => execute(getBatch, { id: batch.id }),
                  staleTime: 30_000,
                });
              }, 50);
            }}
            onMouseLeave={() => clearTimeout(prefetchTimeout.current)}>
            <div className='flex flex-col pt-1.5 pb-2.5 flex-1 justify-center'>
              <div className='flex items-center gap-1'>
                <PortalTooltip id={`batch-status-${batch.id}`}>{t(`batchList.status${batch.status.charAt(0) + batch.status.slice(1).toLowerCase()}`)}</PortalTooltip>
                <div
                  className='w-5 flex items-center justify-center [&>svg]:size-5'
                  data-tooltip-id={`batch-status-${batch.id}`}>
                  {batchStatusMap[batch.status].icon}
                </div>
                <span className='text-left font-medium text-md text-slate-900 dark:text-stone-100'>
                  {batch.label || t('batchList.batchKey', { key: batch.key.toUpperCase() })}
                </span>
              </div>
              <span className='text-slate-500 dark:text-stone-400 text-xs font-medium text-left pl-6 leading-none flex items-center gap-1.5'>
                {t('batchList.uploadedBy')}{' '}
                <span className='flex items-center gap-1 text-white'>
                  <img
                    src={
                      batch.createdBy?.avatar?.url ||
                      getPlaceholderAvatar(batch.createdBy?.email || batch.createdBy?.id || '')
                    }
                    alt={batch.createdBy?.name || t('batchList.unknown')}
                    className='size-4 rounded-full'
                  />
                  {batch.createdBy?.name || t('batchList.unknown')}
                </span>
              </span>
            </div>
            <div className='w-32 flex items-center justify-center text-xs font-medium text-slate-500 dark:text-stone-400'>
              {t('batchList.files', { count: batch.numberOfAttachments })}
            </div>
            <div className='w-42 flex flex-col justify-center text-xs font-medium text-slate-500 dark:text-stone-400 gap-0.5 items-start px-4'>
              <div className='flex items-center gap-1'>
                <Calendar className='size-4 stroke-2' />
                {formatDateTime(batch.createdAt, t)}
              </div>
              <div className='flex items-center gap-1'>
                <Alarm className='size-4 stroke-2' />
                {batch.processingStartedAt ? (
                  <Duration startedAt={batch.processingStartedAt} completedAt={batch.processingCompletedAt} />
                ) : null}
              </div>
            </div>
            {/* <div className='flex justify-between'>
              <span className='text-slate-500'>
                <span className='font-semibold text-slate-900'>{batch.createdBy?.name || 'Unknown'}</span> uploaded{' '}
                <span className='text-slate-900 font-semibold'>
                  {batch.numberOfAttachments} file
                  {batch.numberOfAttachments > 1 ? 's' : ''}
                </span>{' '}
                at <span className='text-slate-900 font-semibold'>{dayjs(batch.createdAt).format('HH:mm')}</span> on{' '}
                <span className='text-slate-900 font-semibold'>{dayjs(batch.createdAt).format('DD/MM/YYYY')}</span>
              </span>
              <Badge variant='secondary' className={cn(statusMap[batch.status].className)}>
                {statusMap[batch.status].icon}
                {statusMap[batch.status].label}
              </Badge>
            </div> */}
          </Link>
        ))}
      </div>
    </>
  );
};

export default BatchList;
